import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import { getApiUrl } from "../config";
import { evaluate } from "mathjs";
import Fraction from "fraction.js";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Tooltip,
  Chip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CalculateIcon from "@mui/icons-material/Calculate";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HistoryIcon from "@mui/icons-material/History";
import SchoolIcon from "@mui/icons-material/School";
import { toast } from "react-toastify";

// ── keyframes ──
const fadeUp = keyframes`from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}`;
const slideIn = keyframes`from{opacity:0;transform:translateX(-100%)}to{opacity:1;transform:translateX(0)}`;
const urgPulse = keyframes`0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,.3)}50%{box-shadow:0 0 0 10px rgba(239,68,68,0)}`;
const optSlide = keyframes`from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}`;

// ── styled ──
const Root = styled(Box)({
  minHeight: "100vh",
  background: "#f0f3f9",
  display: "flex",
  flexDirection: "column",
  fontFamily: "'Sora',sans-serif",
  overflow: "hidden",
});

const TopBar = styled(Box)({
  height: 62,
  background: "#07101f",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 22px",
  position: "sticky",
  top: 0,
  zIndex: 200,
  flexShrink: 0,
  borderBottom: "1px solid rgba(255,255,255,.06)",
  boxShadow: "0 4px 24px rgba(0,0,0,.25)",
});

const ModeSwitch = styled(Box)({
  display: "flex",
  gap: 1,
  background: "rgba(255,255,255,.07)",
  borderRadius: 10,
  p: "3px",
});

const ModeBtn = styled(Button)(({ active }) => ({
  borderRadius: 8,
  textTransform: "none",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 600,
  fontSize: ".82rem",
  padding: "6px 18px",
  transition: "all .22s ease",
  ...(active === "true"
    ? {
        background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
        color: "#fff",
        boxShadow: "0 4px 14px -4px rgba(37,99,235,.5)",
      }
    : {
        background: "transparent",
        color: "rgba(255,255,255,.5)",
        "&:hover": { background: "rgba(255,255,255,.08)", color: "#fff" },
      }),
}));

const TimerChip = styled(Box)(({ urgent }) => ({
  display: "flex",
  alignItems: "center",
  gap: 7,
  background:
    urgent === "true" ? "rgba(239,68,68,.15)" : "rgba(255,255,255,.08)",
  border: `1.5px solid ${
    urgent === "true" ? "rgba(239,68,68,.5)" : "rgba(255,255,255,.1)"
  }`,
  borderRadius: 10,
  padding: "5px 14px",
  backdropFilter: "blur(8px)",
  animation:
    urgent === "true" ? `${urgPulse} 1.4s ease-in-out infinite` : "none",
}));

const CalcSide = styled(Box)(({ open }) => ({
  width: open === "true" ? 288 : 0,
  minWidth: open === "true" ? 288 : 0,
  background: "#0d1829",
  borderRight: "1px solid rgba(255,255,255,.06)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  transition:
    "width .32s cubic-bezier(.22,1,.36,1), min-width .32s cubic-bezier(.22,1,.36,1)",
  flexShrink: 0,
}));

const CalcInner = styled(Box)({
  width: 288,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "18px 14px",
  overflowY: "auto",
});

const CalcScreen = styled(Box)({
  background: "#060e1c",
  borderRadius: 12,
  padding: "12px 14px",
  marginBottom: 12,
  minHeight: 76,
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  justifyContent: "flex-end",
  border: "1px solid rgba(255,255,255,.08)",
});

const CBtn = styled(Button)(({ cv }) => ({
  borderRadius: 9,
  minWidth: 0,
  padding: "9px 4px",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".82rem",
  textTransform: "none",
  transition: "all .16s ease",
  ...(cv === "op" && {
    background: "#1e3a70",
    color: "#93c5fd",
    "&:hover": { background: "#1d4ed8", transform: "translateY(-1px)" },
  }),
  ...(cv === "fn" && {
    background: "#0c3344",
    color: "#67e8f9",
    fontSize: ".72rem",
    "&:hover": { background: "#0e4d66", transform: "translateY(-1px)" },
  }),
  ...(cv === "eq" && {
    background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
    color: "#fff",
    gridColumn: "span 4",
    boxShadow: "0 6px 16px -4px rgba(37,99,235,.5)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 10px 22px -4px rgba(37,99,235,.6)",
    },
  }),
  ...(cv === "cl" && {
    background: "#450a0a",
    color: "#fca5a5",
    "&:hover": { background: "#7f1d1d", transform: "translateY(-1px)" },
  }),
  ...(cv === "num" && {
    background: "#1e2d45",
    color: "#e2e8f0",
    "&:hover": { background: "#253553", transform: "translateY(-1px)" },
  }),
  ...(cv === "mod" && {
    background: "transparent",
    color: "#64748b",
    border: "1px solid rgba(255,255,255,.1)",
    fontSize: ".7rem",
    "&:hover": { background: "rgba(255,255,255,.05)" },
  }),
}));

const QCard = styled(Box)({
  background: "#fff",
  borderRadius: 20,
  border: "1.5px solid #e8edf5",
  padding: "32px 36px 26px",
  boxShadow: "0 4px 28px -8px rgba(15,23,42,.09)",
  animation: `${fadeUp} .32s cubic-bezier(.22,1,.36,1) both`,
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  "@media (max-width:600px)": {
    padding: "18px 16px 16px",
    borderRadius: 14,
  },
});

const OptRow = styled(Box)(({ sel }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 13,
  padding: "13px 16px",
  borderRadius: 12,
  border: `2px solid ${sel ? "#3b82f6" : "#e8edf5"}`,
  background: sel ? "#eff6ff" : "#fafbff",
  cursor: "pointer",
  marginBottom: 9,
  transition: "all .2s ease",
  animation: `${optSlide} .28s cubic-bezier(.22,1,.36,1) both`,
  "&:hover": {
    borderColor: sel ? "#2563eb" : "#93c5fd",
    background: sel ? "#dbeafe" : "#f0f6ff",
    transform: "translateX(4px)",
  },
}));

const OptCircle = styled(Box)(({ sel }) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontWeight: 800,
  fontSize: ".85rem",
  background: sel ? "linear-gradient(135deg,#1d4ed8,#3b82f6)" : "#f1f5f9",
  color: sel ? "#fff" : "#64748b",
  boxShadow: sel ? "0 4px 14px rgba(37,99,235,.35)" : "none",
  transition: "all .2s ease",
}));

const NavDot = styled(Box)(({ answered, current }) => ({
  width: 36,
  height: 36,
  borderRadius: 9,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Sora',sans-serif",
  fontWeight: 700,
  fontSize: ".78rem",
  cursor: "pointer",
  transition: "all .18s ease",
  flexShrink: 0,
  border: current
    ? "2px solid #3b82f6"
    : answered
    ? "2px solid #22c55e"
    : "2px solid #e2e8f0",
  background: current ? "#dbeafe" : answered ? "#f0fdf4" : "#fff",
  color: current ? "#1d4ed8" : answered ? "#15803d" : "#64748b",
  "&:hover": { transform: "scale(1.1)", borderColor: "#93c5fd" },
}));

const TheoryArea = styled("textarea")({
  width: "100%",
  minHeight: 170,
  borderRadius: 12,
  border: "2px solid #e8edf5",
  background: "#fafbff",
  padding: "14px",
  fontFamily: "'Sora',sans-serif",
  fontSize: ".9rem",
  color: "#0f172a",
  resize: "vertical",
  outline: "none",
  transition: "border-color .2s ease, box-shadow .2s ease",
  boxSizing: "border-box",
  "&:focus": {
    borderColor: "#3b82f6",
    boxShadow: "0 0 0 4px rgba(59,130,246,.09)",
    background: "#fff",
  },
});

// ── helpers ──
const toFrac = (n) => {
  try {
    return new Fraction(n).toFraction(true);
  } catch {
    return String(n);
  }
};
const renderContent = (text) => {
  if (!text) return null;
  return text
    .split(/(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\\\([\s\S]+?\\\)|\$[^$]+\$)/g)
    .map((p, i) => {
      if (p.startsWith("$$") && p.endsWith("$$"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\[") && p.endsWith("\\]"))
        return (
          <div key={i} style={{ margin: "8px 0" }}>
            <BlockMath math={p.slice(2, -2)} />
          </div>
        );
      if (p.startsWith("\\(") && p.endsWith("\\)"))
        return <InlineMath key={i} math={p.slice(2, -2)} />;
      if (p.startsWith("$") && p.endsWith("$"))
        return <InlineMath key={i} math={p.slice(1, -1)} />;
      return (
        <span key={i} style={{ whiteSpace: "pre-line" }}>
          {p}
        </span>
      );
    });
};

// ── component ──
export default function ExamStudentPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get("subjectId");
  const studCode = localStorage.getItem("stud_code");
  const [token] = useState(() => JSON.parse(localStorage.getItem("authToken")));

  const [objQs, setObjQs] = useState([]);
  const [thyQs, setThyQs] = useState([]);
  const [objAns, setObjAns] = useState({});
  const [thyAns, setThyAns] = useState({});
  const [name, setName] = useState("");
  const [marks, setMarks] = useState(null);
  const [timeLeft, setTimeLeft] = useState("00:30:00");
  const [timeSec, setTimeSec] = useState(1800);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [mode, setMode] = useState("objective");
  const [idx, setIdx] = useState(0);
  const [calcOpen, setCalcOpen] = useState(false);
  const [showHist, setShowHist] = useState(false);

  const [disp, setDisp] = useState("");
  const [expr, setExpr] = useState("");
  const [ans, setAns] = useState(0);
  const [hist, setHist] = useState([]);
  const [angMode, setAngMode] = useState("DEG");

  const submitRef = useRef();
  const intervalRef = useRef();

  useEffect(() => {
    const link = document.createElement("link");
    link.href =
      "https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&family=DM+Serif+Display:ital@0;1&display=swap";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }, []);

  const startTimer = useCallback((dur) => {
    let t = dur;
    setTimeSec(t);
    intervalRef.current = setInterval(() => {
      t--;
      setTimeSec(t);
      const h = Math.floor(t / 3600),
        m = Math.floor((t % 3600) / 60),
        s = t % 60;
      setTimeLeft(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(
          s
        ).padStart(2, "0")}`
      );
      if (t <= 0) {
        clearInterval(intervalRef.current);
        submitRef.current?.();
      }
    }, 1000);
  }, []);

  const fetchQs = useCallback(async () => {
    setLoading(true);
    const API_URL = getApiUrl();
    try {
      const r = await fetch(
        `${API_URL}/subjects/${studCode}/${subjectId}/questions/`,
        { headers: { Authorization: `Bearer ${token?.access}` } }
      );
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || "Failed to load questions");
        navigate(-1);
        return;
      }
      setName(d.student_name);
      setMarks(d.marks ?? null);
      setObjQs(d.objective_questions);
      setThyQs(d.theory_questions);
      let t = 1800;
      if (d.time_spent) {
        const [hh, mm] = d.time_spent.split(":").map(Number);
        t = (hh * 60 + mm) * 60;
      }
      startTimer(t);
    } catch {
      toast.error("Error loading questions");
    } finally {
      setLoading(false);
    }
  }, [studCode, subjectId, token?.access, startTimer, navigate]);

  useEffect(() => {
    if (!token || !studCode || !subjectId) {
      navigate("/student-login");
      return;
    }
    fetchQs();
    return () => clearInterval(intervalRef.current);
  }, [fetchQs, navigate, studCode, subjectId, token]);

  const handleSubmit = async () => {
    if (!Object.keys(objAns).length && !Object.keys(thyAns).length) {
      toast.error("Answer at least one question.");
      return;
    }
    const API_URL = getApiUrl();
    setSubmitting(true);
    try {
      const r = await fetch(`${API_URL}/submit-exam/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token?.access}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          studCode,
          subject_id: parseInt(subjectId),
          objective_answers: objAns,
          theory_answers: thyAns,
        }),
      });
      if (r.ok) {
        clearInterval(intervalRef.current);
        setSuccess(true);
      } else {
        const d = await r.json();
        toast.error(d.detail || "Submission failed");
      }
    } catch {
      toast.error("Submission error");
    } finally {
      setSubmitting(false);
    }
  };
  useEffect(() => {
    submitRef.current = handleSubmit;
  });

  const append = (v) => setDisp((p) => p + v);
  const clearD = () => setDisp("");
  const delLast = () => setDisp((p) => p.slice(0, -1));

  const calc = useCallback(() => {
    try {
      let e = disp.replace(/ANS/g, ans);
      if (angMode === "DEG")
        e = e
          .replace(/sin\(([^)]*)\)/g, "sin(($1)*pi/180)")
          .replace(/cos\(([^)]*)\)/g, "cos(($1)*pi/180)")
          .replace(/tan\(([^)]*)\)/g, "tan(($1)*pi/180)");
      const res = evaluate(e);
      const frac = toFrac(res);
      setAns(res);
      setExpr(disp + " =");
      setDisp(frac);
      setHist((p) => [...p, `${disp} = ${frac}`]);
    } catch {
      setDisp("Error");
    }
  }, [disp, ans, angMode]);

  useEffect(() => {
    const hk = (e) => {
      if (!calcOpen) return;
      if ("0123456789+-*/().".includes(e.key)) append(e.key);
      if (e.key === "Enter") calc();
      if (e.key === "Backspace") delLast();
    };
    window.addEventListener("keydown", hk);
    return () => window.removeEventListener("keydown", hk);
  }, [calc, calcOpen]);

  const questions = mode === "objective" ? objQs : thyQs;
  const currentQ = questions[idx];
  const totalQ = questions.length;
  const isUrgent = timeSec <= 60;
  const isWarn = timeSec <= 600 && !isUrgent;
  const answeredObj = Object.keys(objAns).length;
  const answeredThy = Object.keys(thyAns).length;
  const logout = () => {
    localStorage.clear();
    navigate("/student-login");
  };

  const calcRows = [
    [
      { l: "sin", a: () => append("sin("), v: "fn" },
      { l: "cos", a: () => append("cos("), v: "fn" },
      { l: "tan", a: () => append("tan("), v: "fn" },
      { l: "log", a: () => append("log("), v: "fn" },
    ],
    [
      { l: "√", a: () => append("sqrt("), v: "fn" },
      { l: "x²", a: () => append("^2"), v: "fn" },
      { l: "xʸ", a: () => append("^"), v: "fn" },
      { l: "x!", a: () => append("!"), v: "fn" },
    ],
    [
      { l: "π", a: () => append("pi"), v: "fn" },
      { l: "e", a: () => append("e"), v: "fn" },
      { l: "ANS", a: () => append("ANS"), v: "fn" },
      { l: "( )", a: () => append("("), v: "fn" },
    ],
    [
      { l: "7", a: () => append("7"), v: "num" },
      { l: "8", a: () => append("8"), v: "num" },
      { l: "9", a: () => append("9"), v: "num" },
      { l: "÷", a: () => append("/"), v: "op" },
    ],
    [
      { l: "4", a: () => append("4"), v: "num" },
      { l: "5", a: () => append("5"), v: "num" },
      { l: "6", a: () => append("6"), v: "num" },
      { l: "×", a: () => append("*"), v: "op" },
    ],
    [
      { l: "1", a: () => append("1"), v: "num" },
      { l: "2", a: () => append("2"), v: "num" },
      { l: "3", a: () => append("3"), v: "num" },
      { l: "−", a: () => append("-"), v: "op" },
    ],
    [
      { l: "0", a: () => append("0"), v: "num" },
      { l: ".", a: () => append("."), v: "num" },
      { l: "⌫", a: delLast, v: "cl" },
      { l: "+", a: () => append("+"), v: "op" },
    ],
  ];

  if (loading)
    return (
      <Root
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          background: "#07101f",
        }}
      >
        <CircularProgress size={50} thickness={4} sx={{ color: "#3b82f6" }} />
        <Typography
          sx={{
            fontFamily: "'Sora',sans-serif",
            color: "rgba(255,255,255,.5)",
            fontSize: ".92rem",
          }}
        >
          Loading your exam…
        </Typography>
      </Root>
    );

  return (
    <Root>
      {/* TOP BAR */}
      <TopBar>
        {/* Left */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mr: 1 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "9px",
                background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SchoolIcon sx={{ color: "#fff", fontSize: 17 }} />
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                color: "#fff",
                fontSize: ".92rem",
                display: { xs: "none", md: "block" },
              }}
            >
              LiteUpper
            </Typography>
          </Box>

          <Tooltip
            title={calcOpen ? "Close Calculator" : "Open Calculator"}
            arrow
          >
            <Box
              onClick={() => setCalcOpen((o) => !o)}
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: calcOpen
                  ? "linear-gradient(135deg,#1d4ed8,#3b82f6)"
                  : "rgba(255,255,255,.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all .22s ease",
                boxShadow: calcOpen
                  ? "0 4px 14px -4px rgba(37,99,235,.5)"
                  : "none",
                "&:hover": {
                  background: calcOpen
                    ? "linear-gradient(135deg,#1e40af,#2563eb)"
                    : "rgba(255,255,255,.14)",
                },
              }}
            >
              <CalculateIcon
                sx={{
                  fontSize: 20,
                  color: calcOpen ? "#fff" : "rgba(255,255,255,.65)",
                }}
              />
            </Box>
          </Tooltip>

          <ModeSwitch>
            <ModeBtn
              active={mode === "objective" ? "true" : "false"}
              onClick={() => {
                setMode("objective");
                setIdx(0);
              }}
            >
              Objective{" "}
              {answeredObj > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.8,
                    background: "rgba(255,255,255,.15)",
                    borderRadius: 100,
                    px: 1,
                    fontSize: ".68rem",
                  }}
                >
                  {answeredObj}/{objQs.length}
                </Box>
              )}
            </ModeBtn>
            <ModeBtn
              active={mode === "theory" ? "true" : "false"}
              onClick={() => {
                setMode("theory");
                setIdx(0);
              }}
            >
              Theory{" "}
              {answeredThy > 0 && (
                <Box
                  component="span"
                  sx={{
                    ml: 0.8,
                    background: "rgba(255,255,255,.15)",
                    borderRadius: 100,
                    px: 1,
                    fontSize: ".68rem",
                  }}
                >
                  {answeredThy}/{thyQs.length}
                </Box>
              )}
            </ModeBtn>
          </ModeSwitch>
        </Box>

        {/* Center */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              color: "#e2e8f0",
              fontSize: ".9rem",
              lineHeight: 1.2,
            }}
          >
            {name}
          </Typography>
          {marks !== null && (
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontSize: ".7rem",
                color: "rgba(255,255,255,.4)",
              }}
            >
              Score: <b style={{ color: "#60a5fa" }}>{marks}</b>
            </Typography>
          )}
        </Box>

        {/* Right */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TimerChip urgent={isUrgent ? "true" : "false"}>
            <AccessTimeIcon
              sx={{
                fontSize: 16,
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#60a5fa",
              }}
            />
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: ".95rem",
                color: isUrgent ? "#f87171" : isWarn ? "#fbbf24" : "#e2e8f0",
                letterSpacing: ".04em",
              }}
            >
              {timeLeft}
            </Typography>
          </TimerChip>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            endIcon={
              submitting ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon sx={{ fontSize: ".95rem !important" }} />
              )
            }
            sx={{
              borderRadius: 10,
              textTransform: "none",
              fontFamily: "'Sora',sans-serif",
              fontWeight: 700,
              fontSize: ".82rem",
              py: "7px",
              px: 2.2,
              background: "linear-gradient(105deg,#15803d,#22c55e)",
              boxShadow: "0 6px 16px -4px rgba(22,163,74,.45)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 10px 22px -4px rgba(22,163,74,.55)",
              },
              transition: "all .22s ease",
              color: "#fff",
            }}
            variant="contained"
          >
            {submitting ? "" : "Submit"}
          </Button>
        </Box>
      </TopBar>

      {/* BODY */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* CALCULATOR */}
        <CalcSide open={calcOpen ? "true" : "false"}>
          {calcOpen && (
            <CalcInner
              sx={{
                animation: `${slideIn} .3s cubic-bezier(.22,1,.36,1) both`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalculateIcon sx={{ color: "#60a5fa", fontSize: 17 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#e2e8f0",
                      fontSize: ".86rem",
                    }}
                  >
                    Calculator
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 0.8 }}>
                  {["DEG", "RAD"].map((m) => (
                    <CBtn
                      key={m}
                      cv="mod"
                      onClick={() => setAngMode(m)}
                      sx={{
                        background: angMode === m ? "#1e3a70" : "transparent",
                        color: angMode === m ? "#93c5fd" : "#475569",
                      }}
                    >
                      {m}
                    </CBtn>
                  ))}
                </Box>
              </Box>

              <CalcScreen>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".7rem",
                    color: "#334155",
                    mb: 0.4,
                    wordBreak: "break-all",
                  }}
                >
                  {expr || "\u00A0"}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontWeight: 800,
                    fontSize: disp.length > 12 ? "1.05rem" : "1.55rem",
                    color: disp === "Error" ? "#f87171" : "#e2e8f0",
                    wordBreak: "break-all",
                    textAlign: "right",
                  }}
                >
                  {disp || "0"}
                </Typography>
              </CalcScreen>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 0.8,
                  mb: 0.8,
                }}
              >
                <CBtn cv="cl" onClick={clearD} fullWidth>
                  AC
                </CBtn>
                <CBtn cv="fn" onClick={() => append("(")} fullWidth>
                  ( )
                </CBtn>
              </Box>

              {calcRows.map((row, ri) => (
                <Box
                  key={ri}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: "5px",
                    mb: "5px",
                  }}
                >
                  {row.map((b, bi) => (
                    <CBtn key={bi} cv={b.v} onClick={b.a} fullWidth>
                      {b.l}
                    </CBtn>
                  ))}
                </Box>
              ))}

              <Box sx={{ mt: "5px" }}>
                <CBtn
                  cv="eq"
                  onClick={calc}
                  fullWidth
                  sx={{ py: 1.2, fontSize: "1.05rem" }}
                >
                  =
                </CBtn>
              </Box>

              <Box
                onClick={() => setShowHist((h) => !h)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 2,
                  cursor: "pointer",
                  color: "#475569",
                  "&:hover": { color: "#64748b" },
                }}
              >
                <HistoryIcon sx={{ fontSize: 15 }} />
                <Typography
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: ".72rem",
                    fontWeight: 600,
                  }}
                >
                  {showHist ? "Hide" : "Show"} History
                </Typography>
              </Box>

              {showHist && hist.length > 0 && (
                <Box
                  sx={{
                    mt: 0.8,
                    background: "#060e1c",
                    borderRadius: 9,
                    p: "9px 11px",
                    maxHeight: 110,
                    overflowY: "auto",
                  }}
                >
                  {hist
                    .slice(-8)
                    .reverse()
                    .map((h, i) => (
                      <Typography
                        key={i}
                        sx={{
                          fontFamily: "'Sora',sans-serif",
                          fontSize: ".7rem",
                          color: "#475569",
                          mb: 0.4,
                          borderBottom: "1px solid #0d1829",
                          pb: 0.4,
                        }}
                      >
                        {h}
                      </Typography>
                    ))}
                </Box>
              )}
            </CalcInner>
          )}
        </CalcSide>

        {/* MAIN */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            minWidth: 0,
            overflowY: "auto",
            p: { xs: "14px 14px", md: "22px 28px" },
          }}
        >
          {/* Progress */}
          <Box sx={{ mb: 2.5 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}
            >
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".72rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: ".07em",
                }}
              >
                {mode === "objective" ? "Objective" : "Theory"} · Q{idx + 1} of{" "}
                {totalQ}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontSize: ".72rem",
                  color: "#94a3b8",
                }}
              >
                {mode === "objective" ? answeredObj : answeredThy}/{totalQ}{" "}
                answered
              </Typography>
            </Box>
            <Box
              sx={{
                height: 3,
                background: "#e2e8f0",
                borderRadius: 100,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  width: `${
                    totalQ > 0
                      ? ((mode === "objective" ? answeredObj : answeredThy) /
                          totalQ) *
                        100
                      : 0
                  }%`,
                  background: "linear-gradient(90deg,#1d4ed8,#3b82f6)",
                  borderRadius: 100,
                  transition: "width .4s ease",
                }}
              />
            </Box>
          </Box>

          {totalQ === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: "2.5rem" }}>📭</Typography>
              <Typography
                sx={{
                  fontFamily: "'Sora',sans-serif",
                  fontWeight: 700,
                  color: "#64748b",
                  fontSize: "1rem",
                }}
              >
                No {mode} questions assigned
              </Typography>
            </Box>
          ) : currentQ ? (
            <>
              <QCard key={currentQ?.id}>
                {/* Q header */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "12px",
                      background: "linear-gradient(135deg,#1d4ed8,#3b82f6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 6px 16px -4px rgba(37,99,235,.35)",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 800,
                        color: "#fff",
                        fontSize: ".88rem",
                      }}
                    >
                      {idx + 1}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".68rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                      }}
                    >
                      {mode === "objective"
                        ? "Multiple Choice"
                        : `Theory · ${currentQ.marks ?? ""} Marks`}
                    </Typography>
                  </Box>
                  {mode === "objective" && objAns[currentQ.id] && (
                    <Chip
                      label="Answered ✓"
                      size="small"
                      sx={{
                        background: "#f0fdf4",
                        color: "#16a34a",
                        border: "1px solid #bbf7d0",
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".7rem",
                      }}
                    />
                  )}
                  {mode === "theory" && thyAns[currentQ.id] && (
                    <Chip
                      label="Answered ✓"
                      size="small"
                      sx={{
                        background: "#f0fdf4",
                        color: "#16a34a",
                        border: "1px solid #bbf7d0",
                        fontFamily: "'Sora',sans-serif",
                        fontWeight: 700,
                        fontSize: ".7rem",
                      }}
                    />
                  )}
                </Box>

                <Box
                  sx={{
                    fontFamily: "'Sora',sans-serif",
                    fontSize: "1rem",
                    color: "#0f172a",
                    lineHeight: 1.78,
                    mb: 3,
                  }}
                >
                  {renderContent(
                    mode === "objective"
                      ? currentQ.question_text
                      : currentQ.Question
                  )}
                </Box>

                {(currentQ.image_url || currentQ.question_image) && (
                  <Box
                    sx={{
                      mb: 3,
                      borderRadius: 12,
                      overflow: "hidden",
                      border: "1px solid #e2e8f0",
                      maxWidth: 460,
                    }}
                  >
                    <img
                      src={currentQ.image_url || currentQ.question_image}
                      alt="question"
                      style={{ width: "100%", display: "block" }}
                    />
                  </Box>
                )}

                {mode === "objective" && (
                  <Box sx={{ flex: 1 }}>
                    {["A", "B", "C", "D"].map((opt, oi) => {
                      const sel = objAns[currentQ.id] === opt;
                      return (
                        <OptRow
                          key={opt}
                          sel={sel}
                          onClick={() =>
                            setObjAns((p) => ({ ...p, [currentQ.id]: opt }))
                          }
                          sx={{ animationDelay: `${oi * 0.06}s` }}
                        >
                          <OptCircle sel={sel}>{opt}</OptCircle>
                          <Box
                            sx={{
                              fontFamily: "'Sora',sans-serif",
                              fontSize: ".92rem",
                              color: "#334155",
                              lineHeight: 1.65,
                              pt: "5px",
                            }}
                          >
                            {renderContent(
                              currentQ[`option_${opt.toLowerCase()}`]
                            )}
                          </Box>
                        </OptRow>
                      );
                    })}
                  </Box>
                )}

                {mode === "theory" && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "'Sora',sans-serif",
                        fontSize: ".68rem",
                        fontWeight: 700,
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: ".07em",
                        mb: 1,
                      }}
                    >
                      Your Answer
                    </Typography>
                    <TheoryArea
                      placeholder="Type your answer here…"
                      value={thyAns[currentQ.id] || ""}
                      onChange={(e) =>
                        setThyAns((p) => ({
                          ...p,
                          [currentQ.id]: e.target.value,
                        }))
                      }
                    />
                  </Box>
                )}
              </QCard>

              {/* NAVIGATION */}
              <Box
                sx={{
                  mt: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Button
                    onClick={() => setIdx((i) => i - 1)}
                    disabled={idx === 0}
                    startIcon={<ChevronLeftIcon />}
                    sx={{
                      borderRadius: 10,
                      textTransform: "none",
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 600,
                      color: "#64748b",
                      border: "1.5px solid #e2e8f0",
                      px: 2.5,
                      py: 1,
                      "&:hover": {
                        background: "#f1f5f9",
                        borderColor: "#cbd5e1",
                      },
                      "&.Mui-disabled": { opacity: 0.35 },
                    }}
                  >
                    Previous
                  </Button>
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".82rem",
                      color: "#94a3b8",
                      fontWeight: 600,
                    }}
                  >
                    {idx + 1} / {totalQ}
                  </Typography>
                  <Button
                    onClick={() => setIdx((i) => i + 1)}
                    disabled={idx === totalQ - 1}
                    endIcon={<ChevronRightIcon />}
                    sx={{
                      borderRadius: 10,
                      textTransform: "none",
                      fontFamily: "'Sora',sans-serif",
                      fontWeight: 700,
                      color: "#fff",
                      background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                      px: 2.5,
                      py: 1,
                      boxShadow: "0 6px 16px -4px rgba(37,99,235,.35)",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 22px -4px rgba(37,99,235,.45)",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.35,
                        background: "#e2e8f0",
                        color: "#94a3b8",
                        boxShadow: "none",
                      },
                      transition: "all .22s ease",
                    }}
                  >
                    Next
                  </Button>
                </Box>

                {/* Number pad */}
                <Box
                  sx={{
                    background: "#fff",
                    borderRadius: 16,
                    border: "1px solid #e8edf5",
                    p: "12px 16px",
                    boxShadow: "0 2px 12px -4px rgba(15,23,42,.06)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Sora',sans-serif",
                      fontSize: ".66rem",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                      letterSpacing: ".09em",
                      mb: 1.2,
                    }}
                  >
                    Jump to question
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.9 }}>
                    {questions.map((q, i) => {
                      const a =
                        mode === "objective" ? objAns[q.id] : thyAns[q.id];
                      return (
                        <NavDot
                          key={q.id}
                          answered={!!a}
                          current={i === idx}
                          onClick={() => setIdx(i)}
                        >
                          {i + 1}
                        </NavDot>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: "flex", gap: 2.5, mt: 1.5 }}>
                    {[
                      { color: "#3b82f6", label: "Current" },
                      { color: "#22c55e", label: "Answered" },
                      { color: "#e2e8f0", label: "Unanswered" },
                    ].map((l) => (
                      <Box
                        key={l.label}
                        sx={{ display: "flex", alignItems: "center", gap: 0.7 }}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: 3,
                            background: l.color,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: "'Sora',sans-serif",
                            fontSize: ".67rem",
                            color: "#94a3b8",
                          }}
                        >
                          {l.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </>
          ) : null}
        </Box>
      </Box>

      {/* SUCCESS MODAL */}
      {success && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(7,16,31,.75)",
            backdropFilter: "blur(8px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              background: "#fff",
              borderRadius: "24px",
              p: "46px 42px",
              textAlign: "center",
              width: 360,
              boxShadow: "0 48px 96px -24px rgba(15,23,42,.3)",
              border: "1px solid #e2e8f0",
              animation: `${fadeUp} .4s cubic-bezier(.22,1,.36,1) both`,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "20px",
                background: "linear-gradient(135deg,#f0fdf4,#dcfce7)",
                border: "2px solid #bbf7d0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 22px",
                fontSize: "2rem",
              }}
            >
              🎉
            </Box>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                fontWeight: 800,
                fontSize: "1.4rem",
                color: "#0f172a",
                mb: 0.8,
              }}
            >
              Exam Submitted!
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Sora',sans-serif",
                color: "#64748b",
                fontSize: ".88rem",
                lineHeight: 1.65,
                mb: 3.5,
              }}
            >
              Your responses have been successfully recorded. Well done!
            </Typography>
            <Button
              onClick={logout}
              variant="contained"
              fullWidth
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontFamily: "'Sora',sans-serif",
                fontWeight: 700,
                py: 1.55,
                fontSize: ".93rem",
                background: "linear-gradient(105deg,#1d4ed8,#3b82f6)",
                boxShadow: "0 10px 24px -6px rgba(37,99,235,.4)",
                "&:hover": { transform: "translateY(-2px)" },
                transition: "all .25s ease",
              }}
            >
              Done
            </Button>
          </Box>
        </Box>
      )}
    </Root>
  );
}
